import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ScenarioRunClient, ScenarioRunDocument, ScenarioRunScenario, ScenarioRunState } from '../../api/api';
import { stopScenarioRun } from '../../api/scenarioStop';
import { copyScenarioRunDiagnostics, fetchScenarioRunDiagnostics } from '../../api/scenarioRunDiagnostics';
import { useAxiosSwagger } from '../../hooks/useAxiosSwagger';
import { Lane } from '../data/cockpit';
import { ago, duration, shortBuild, when } from '../data/format';
import { apiError, usePoll } from '../data/usePoll';
import { Card, Empty, EnvChip, ErrorNotice, Icon, Kv, Loading, Mark, Spinner } from '../components/ui';
import { DOC_COLORS, FlowItem, FlowLook, FlowTimeline } from '../components/FlowTimeline';
import { v2 } from '../paths';

/** What kind of document the runner made, in the words of the screen. */
/** Not finished and not updated for this long: the runner is gone. */
const STALE_MS = 10 * 60 * 1000;

const NOUN: [RegExp, string][] = [
    [/credit/i, 'Creditnota'],
    [/refund/i, 'Terugbetaling'],
    [/invoice/i, 'Factuur'],
    [/payment|transaction/i, 'Betaling'],
    [/customer/i, 'Klant'],
    [/subscription/i, 'Abonnement'],
];

export const documentNoun = (name?: string) => NOUN.find(([re]) => re.test(name ?? ''))?.[1] ?? (name ? name[0].toUpperCase() + name.slice(1) : 'Document');

/** Each stage the runner writes, as a lane, a look and what happened to the document. Same stages and words as scenarioFlowModel. */
const STAGES: Record<string, { lane: Lane; app?: string; look: FlowLook; title: (noun: string) => string; direct?: boolean }> = {
    chargebee: { lane: 'cb', look: 'ok', title: (n) => `${n} aangemaakt` },
    received: { lane: 'cn', app: 'cbrcv', look: 'busy', title: () => 'Webhook verwerken' },
    held: { lane: 'cn', app: 'cbrcv', look: 'wait', title: () => 'Wacht op klant in Exact' },
    'in-connector': { lane: 'cn', app: 'cbrcv', look: 'ok', title: () => 'In queue voor exsnd' },
    retrying: { lane: 'cn', app: 'exsnd', look: 'wait', title: () => 'Mislukt, probeert opnieuw' },
    failed: { lane: 'cn', app: 'exsnd', look: 'bad', title: () => 'Mislukt, opgegeven' },
    booked: { lane: 'ex', look: 'ok', title: (n) => `${n} geboekt` },
    handled: { lane: 'ex', look: 'ok', title: () => 'Niets te boeken' },
    elsewhere: { lane: 'ex', look: 'ok', title: () => 'Door een andere connector geboekt', direct: true },
    'paid-in-exact': { lane: 'ex', look: 'ok', title: () => 'Betaald in Exact' },
    'exact-webhook': { lane: 'cn', app: 'exrcv', look: 'busy', title: () => 'Webhook verwerken' },
    'to-chargebee': { lane: 'cn', app: 'exrcv', look: 'ok', title: () => 'In queue voor cbsnd' },
    'in-chargebee': { lane: 'cb', look: 'ok', title: (n) => `${n} bijgewerkt` },
};

const statusOf = (s?: string) => (s ?? '').toLowerCase();
const isRunning = (s?: string) => ['running', 'verifying'].includes(statusOf(s));

const scenarioMark = (s?: string): { tone: 'ok' | 'bad' | 'wait' | 'todo'; mark?: string } => {
    const v = statusOf(s);
    if (v === 'passed') return { tone: 'ok' };
    if (v === 'failed') return { tone: 'bad' };
    if (isRunning(v)) return { tone: 'wait', mark: '↻' };
    if (v === 'skipped') return { tone: 'todo', mark: '–' };
    return { tone: 'todo' };
};

const STATUS_LABEL: Record<string, string> = { passed: 'geslaagd', failed: 'mislukt', running: 'loopt', verifying: 'controleert', skipped: 'overgeslagen', pending: 'wacht' };

/** "invoice-webhook-twice-receivables-on" with profile "receivables-on" → "invoice webhook twice". */
export const scenarioName = (s: ScenarioRunScenario) => {
    const id = s.id ?? '';
    const base = s.profile && id.endsWith(`-${s.profile}`) ? id.slice(0, -(s.profile.length + 1)) : id;
    return base.replace(/[-_]+/g, ' ').trim() || s.description || 'scenario';
};

/** Every document's history as cards in its lanes, in the order it happened, with the NU line for a running scenario. */
const toFlow = (scenario: ScenarioRunScenario, running: boolean) => {
    const docs = (scenario.documents ?? []).filter((d) => d.id || d.name);
    const documents = docs.map((d, i) => ({
        id: d.id ?? d.name ?? String(i),
        label: `${documentNoun(d.name)}${d.id ? ` ${d.id}` : ''}`,
        noun: documentNoun(d.name),
        color: DOC_COLORS[i % DOC_COLORS.length],
    }));
    const events = docs
        .flatMap((d: ScenarioRunDocument, i) =>
            (d.history?.length ? d.history : [{ stage: d.stage, atUtc: d.sinceUtc, detail: d.detail }]).map((h) => ({ doc: documents[i], stage: h.stage ?? 'chargebee', at: h.atUtc, detail: h.detail }))
        )
        .sort((a, b) => (a.at ?? '').localeCompare(b.at ?? ''));
    // Per document, in the order they first show up, and in time within each: each line stays within its own heading
    // instead of running past every other document of the run.
    const first = new Map<string, number>();
    events.forEach((e, i) => !first.has(e.doc.id) && first.set(e.doc.id, i));
    const sorted = events
        .map((e, i) => ({ e, i }))
        .sort((a, b) => first.get(a.e.doc.id)! - first.get(b.e.doc.id)! || a.i - b.i)
        .map(({ e }) => e);

    const lastOf = new Map<string, number>();
    sorted.forEach((e, i) => lastOf.set(e.doc.id, i));

    const items: FlowItem[] = [];
    sorted.forEach((e, i) => {
        const stage = STAGES[e.stage] ?? STAGES.chargebee;
        const isLatest = lastOf.get(e.doc.id) === i;
        if (i === 0 || sorted[i - 1].doc.id !== e.doc.id) items.push({ phase: e.doc.label });
        items.push({
            at: e.at,
            documentId: e.doc.id,
            lane: stage.lane,
            app: stage.app,
            direct: stage.direct,
            look: isLatest && running && stage.look === 'ok' && stage.lane !== 'ex' && e.stage !== 'in-chargebee' ? 'busy' : stage.look,
            title: stage.title(e.doc.noun),
            // The heading says which document; the runner's own words, when there are any, say more.
            detail: e.detail || undefined,
        });
    });
    if (running) items.push({ now: when(new Date()) });
    return { items, documents };
};

/** The scenarios of the run by name, with one pill per profile it ran under. */
const groupScenarios = (scenarios: ScenarioRunScenario[]) => {
    const groups: { name: string; items: ScenarioRunScenario[] }[] = [];
    scenarios.forEach((s) => {
        const name = scenarioName(s);
        const group = groups.find((g) => g.name === name);
        if (group) group.items.push(s);
        else groups.push({ name, items: [s] });
    });
    return groups;
};

const worst = (items: ScenarioRunScenario[]) =>
    items.find((s) => statusOf(s.status) === 'failed')?.status ?? items.find((s) => isRunning(s.status))?.status ?? (items.every((s) => statusOf(s.status) === 'passed') ? 'passed' : items[0]?.status);

/** The phases as one bar: done, now, still to come; the name only for the one that matters. */
const Phases = ({ scenario }: { scenario: ScenarioRunScenario }) => {
    const phases = scenario.phases ?? [];
    if (phases.length === 0) return null;
    const current = phases.findIndex((p) => !p.finishedAtUtc);
    const done = statusOf(scenario.status) === 'passed' || statusOf(scenario.status) === 'failed';
    const at = current < 0 ? phases.length - 1 : current;

    return (
        <div className="phasebar">
            <span className="segs">
                {phases.map((p, i) => (
                    <i key={p.id ?? i} className={p.finishedAtUtc ? 'done' : i === current && !done ? 'run' : ''} title={`${i + 1}. ${p.name}`} />
                ))}
            </span>
            <span className="mut">
                {current < 0 || done ? `${phases.length} fasen doorlopen` : <>Fase {at + 1} van {phases.length} · <span style={{ color: '#E6EAF0' }}>{phases[at]?.name}</span></>}
            </span>
        </div>
    );
};

export const RunPage = () => {
    const { customer = '', environment = '', runId = '' } = useParams();
    const axios = useAxiosSwagger();
    const [sel, setSel] = useState<string>();
    const [note, setNote] = useState<string>();
    const [stopping, setStopping] = useState(false);
    const [live, setLive] = useState(true);

    // Every 2.5s while it runs; once it finished or went quiet, no more.
    const run = usePoll<ScenarioRunState>(
        axios ? () => new ScenarioRunClient(undefined, axios).scenarioRun(customer, environment, runId) : null,
        live ? 2500 : 0,
        [axios, customer, environment, runId],
        `run/${customer}/${environment}/${runId}`
    );
    const state = run.data;
    // Not finished but silent for ten minutes: the runner is gone, as the run list says too.
    const quiet = !!state && !state.finishedAtUtc && !!state.updatedAtUtc && Date.now() - new Date(state.updatedAtUtc).getTime() > STALE_MS;
    const running = !!state && !state.finishedAtUtc && !quiet;
    useEffect(() => setLive(!state || running), [state, running]);
    const scenarios = useMemo(() => state?.scenarios ?? [], [state]);
    const groups = useMemo(() => groupScenarios(scenarios), [scenarios]);
    const selected =
        scenarios.find((s) => s.id === sel) ??
        scenarios.find((s) => isRunning(s.status)) ??
        scenarios.find((s) => statusOf(s.status) === 'failed') ??
        scenarios[0];
    const flow = useMemo(() => (selected ? toFlow(selected, running && isRunning(selected.status)) : undefined), [selected, running]);

    const totals = state?.totals;
    const stop = async () => {
        if (!axios || !window.confirm('Deze run stoppen? Wat al in Chargebee en Exact staat, blijft staan.')) return;
        setStopping(true);
        try {
            await stopScenarioRun(axios, customer, environment, runId);
            setNote(undefined);
            run.reload();
        } catch (e) {
            setNote(`Stoppen lukte niet: ${apiError(e)}`);
        } finally {
            setStopping(false);
        }
    };
    const copy = async () => {
        if (!axios) return;
        try {
            await copyScenarioRunDiagnostics(fetchScenarioRunDiagnostics(axios, customer, environment, runId));
            setNote('Diagnose gekopieerd');
        } catch {
            setNote('Kopiëren lukte niet');
        }
    };

    const secs = state?.startedAtUtc ? Math.round(((state.finishedAtUtc ? new Date(state.finishedAtUtc).getTime() : Date.now()) - new Date(state.startedAtUtc).getTime()) / 1000) : 0;
    const done = (totals?.passed ?? 0) + (totals?.failed ?? 0);
    // Finished before every scenario had its turn: stopped, not done.
    const stopped = !running && done < (totals?.total ?? scenarios.length);

    return (
        <>
            <div className="toolbar">
                <Link className="btn sm ghost" to={v2.scenarios()}>
                    <Icon name="back" size={14} />
                    Scenario's
                </Link>
                <span className="mut" style={{ fontSize: 12 }} title={state?.startedAtUtc ? when(state.startedAtUtc) : undefined}>
                    {state?.startedAtUtc ? `gestart ${ago(state.startedAtUtc)} geleden` : ''}
                </span>
            </div>

            <section className="card runhead">
                <div className="cb hrow" style={{ gap: 18 }}>
                    <div style={{ minWidth: 0 }}>
                        <div className="hrow" style={{ gap: 8 }}>
                            <span className="h2">{state?.suiteName ?? 'Run'}</span>
                            <EnvChip kind="lab" environment={`${customer} ${environment}`} />
                        </div>
                        <div className="mono mut" style={{ fontSize: 12, marginTop: 4 }}>{runId}</div>
                    </div>
                    {state ? (
                        <span className={running ? 'chip info' : totals?.failed ? 'chip bad' : stopped ? 'chip' : 'chip ok'}>
                            {running && <Spinner />}
                            {running ? 'Loopt' : quiet ? 'Geen teken van leven' : stopped ? 'Gestopt' : 'Klaar'} · {duration(secs)}
                        </span>
                    ) : (
                        <span className="chip">
                            <Spinner />
                            Laden
                        </span>
                    )}
                    {state && (
                        <div style={{ flex: '1 1 220px', minWidth: 180 }}>
                            <div className="segs">
                                {scenarios.map((s) => (
                                    <i key={s.id} className={statusOf(s.status) === 'passed' ? 'done' : statusOf(s.status) === 'failed' ? 'fail' : isRunning(s.status) ? 'run' : ''} />
                                ))}
                            </div>
                            <div className="hrow mono" style={{ gap: 12, fontSize: 12, marginTop: 6 }}>
                                <span className="ok">{totals?.passed ?? 0} groen</span>
                                <span className={totals?.failed ? 'bad' : 'mut'}>{totals?.failed ?? 0} rood</span>
                                <span className="mut">{done} / {totals?.total ?? scenarios.length}</span>
                            </div>
                        </div>
                    )}
                    <Kv style={{ gridTemplateColumns: 'max-content max-content' }} rows={[['Exact minuut', <span className="mono">{state?.exactLimits?.minutelyRemaining ?? '–'} / {state?.exactLimits?.minutelyLimit ?? 60}</span>], ['Exact dag', <span className="mono">{state?.exactLimits?.dailyRemaining ?? '–'}</span>]]} />
                    <button className="btn ghost" onClick={copy}>Diagnose kopiëren</button>
                    {state?.canStop && (
                        <button className="btn danger" onClick={stop} disabled={stopping}>
                            {stopping && <Spinner />}
                            Stoppen
                        </button>
                    )}
                </div>
                {(note || (running && state?.currentActivity)) && <div className="mut" style={{ padding: '0 16px 12px', fontSize: 13 }}>{note ?? state?.currentActivity}</div>}
            </section>

            <ErrorNotice error={run.error} />
            {run.pending && <Loading>Run ophalen…</Loading>}

            {state && (
                <div className="runbody">
                    <Card title="Scenario's" right={<span className="mono mut" style={{ fontSize: 12 }}>{groups.length}</span>}>
                        {groups.map((g) => {
                            const m = scenarioMark(worst(g.items));
                            const active = g.items.some((s) => s.id === selected?.id);
                            // One scenario whose name already says its profile ("tos prod invoice") needs no pill.
                            const profile = g.items[0].profile;
                            const single = g.items.length === 1 && (!profile || g.name.includes(profile.replace(/[-_]+/g, ' ')));
                            return (
                                <div key={g.name} className={`sgroup ${active ? 'on' : ''}`}>
                                    <button className="sname" onClick={() => setSel(g.items.find((s) => statusOf(s.status) === 'failed')?.id ?? g.items[0].id)} title={g.items[0].description}>
                                        <Mark tone={m.tone}>{m.mark}</Mark>
                                        <span>{g.name}</span>
                                    </button>
                                    {!single && (
                                        <div className="pills">
                                            {g.items.map((s) => {
                                                const sm = scenarioMark(s.status);
                                                return (
                                                    <button key={s.id} className={`pill ${sm.tone} ${s.id === selected?.id ? 'on' : ''}`} onClick={() => setSel(s.id)} title={`${s.profile ?? s.id}: ${STATUS_LABEL[statusOf(s.status)] ?? s.status}`}>
                                                        {s.profile ?? s.id}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </Card>

                    <div className="stack">
                        {selected && (
                            <Card
                                title={
                                    <div style={{ minWidth: 0 }}>
                                        <div className="hrow" style={{ gap: 8 }}>
                                            <span className="h2">{scenarioName(selected)}</span>
                                            {selected.profile && <span className="chip">{selected.profile}</span>}
                                        </div>
                                        {selected.description && <div className="mut" style={{ fontSize: 13, marginTop: 4, maxWidth: 900 }}>{selected.description}</div>}
                                    </div>
                                }
                                right={
                                    <span className={`chip ${scenarioMark(selected.status).tone === 'ok' ? 'ok' : scenarioMark(selected.status).tone === 'bad' ? 'bad' : 'info'}`}>
                                        {isRunning(selected.status) && <Spinner />}
                                        {STATUS_LABEL[statusOf(selected.status)] ?? selected.status}
                                    </span>
                                }
                            >
                                <div className="cb stack" style={{ gap: 14 }}>
                                    <Phases scenario={selected} />
                                    {selected.waitingUntilUtc && <div className="chip warn" style={{ alignSelf: 'flex-start' }}>wacht tot {when(selected.waitingUntilUtc)}</div>}
                                    {(selected.errors ?? []).length > 0 && (
                                        <div className="notice">
                                            {(selected.errors ?? []).slice(0, 3).map((e, i) => (
                                                <div key={i}>{e}</div>
                                            ))}
                                        </div>
                                    )}
                                    {flow && flow.items.length > 0 ? <FlowTimeline items={flow.items} documents={flow.documents} /> : <Empty>Nog geen documenten gezien.</Empty>}
                                </div>
                            </Card>
                        )}

                        {selected && (selected.checks ?? []).length > 0 && (
                            <Card title="Controles" right={<span className="mut" style={{ fontSize: 12 }}>wat de runner terugleest</span>}>
                                <div className="tw">
                                    <table className="tbl">
                                        <thead><tr><th /><th>Controle</th><th>Verwacht</th><th>Gezien</th></tr></thead>
                                        <tbody>
                                            {(selected.checks ?? []).map((c, i) => {
                                                const m = scenarioMark(c.status);
                                                return (
                                                    <tr key={i}>
                                                        <td style={{ width: 28 }}><Mark tone={m.tone}>{m.mark}</Mark></td>
                                                        <td>{c.name}<div className="mut" style={{ fontSize: 12 }}>{c.oracle}</div></td>
                                                        <td className="mono">{c.expected}</td>
                                                        <td className="mono">{c.actual ?? '…'}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </Card>
                        )}

                        <Card title="Run">
                            <div className="cb g2e">
                                <Kv rows={[['Build', <span className="mono">{shortBuild(state.buildVersion) || '–'}</span>], ['Gestart', `${ago(state.startedAtUtc)} geleden`]]} />
                                <Kv rows={[['Profielen', (state.profiles ?? []).map((p) => p.id).join(', ') || '–'], ['Exact-calls', String(state.exactCalls?.total ?? '–')]]} />
                            </div>
                        </Card>
                    </div>
                </div>
            )}
        </>
    );
};
